/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Auth } from "aws-amplify";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import logo from "src/assets/img/jts.svg";
import routes from "src/routes/routes";

/**
 * The Site Wrapper component
 *
 * @author Thais Watanabe
 */
const Header = (props) => {
  const [isMenuOn, setIsMenuOn] = useState(false);
  const [loggedUser, setLoggedUser] = useState("");
  const [loggedUserGroup, setLoggedUserGroup] = useState("");

  const history = useNavigate();

  /**
   * useEffect
   */
  useEffect(() => {
    return () => {
      setIsMenuOn("");
    };
  }, []);

  /**
   * useEffect
   */
  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((result) => {
        setLoggedUserGroup(
          result.signInUserSession.accessToken.payload["cognito:groups"][0]
        );
        setLoggedUser(result.username);
      })
      .catch((error) => console.log(error));
    return () => {
      setLoggedUser("");
    };
  }, []);

  /**
   * signOut
   */
  async function signOut() {
    try {
      await Auth.signOut();
    } catch (error) {
      console.log("Error: ", error);
    }
  }

  /**
   * MobileMenuButton
   */
  function MobileMenuButton() {
    return (
      <button
        className="bg-teal-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-teal-800 focus:ring-white"
        onClick={() => setIsMenuOn(!isMenuOn)}
      >
        <span className="sr-only">Open main menu</span>

        {/* <!-- Heroicon name: menu --> */}
        <Bars3Icon className={`${isMenuOn ? "hidden" : "block"} h-6 w-6`} />

        {/* <!-- Heroicon name: x --> */}
        <XMarkIcon className={`${isMenuOn ? "block" : "hidden"} h-6 w-6`} />
      </button>
    );
  }
  /**
   * LoggedUserDropdown
   */
  function LoggedUserDropdown() {
    return (
      <Menu as="div" className="ml-3 relative">
        {({ open }) => (
          <>
            <div>
              <Menu.Button className="max-w-xs bg-teal-800 rounded-full flex items-center text-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-teal-800 focus:ring-white">
                <span className="sr-only">Open user menu</span>
                <div className="bg-white h-10 w-10 rounded-full flex">
                  <p className="text-center text-gray-800 text-2xl m-auto">
                    {loggedUser.length > 0 &&
                      loggedUser.charAt(0).toUpperCase()}
                  </p>
                </div>
              </Menu.Button>
            </div>
            <Transition
              show={open}
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                static
                className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                {/* <AmplifySignOut buttonText="ログアウト" /> */}
                <button
                  type="button"
                  className="w-full px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  onClick={() => signOut()}
                >
                  ログアウト
                </button>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    );
  }

  return (
    <div className="bg-teal-800 pb-32 print:bg-white">
      <nav className="bg-teal-800 print:hidden">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <div className="border-b border-teal-700">
            <div className="flex items-center justify-between h-16 px-4 sm:px-0">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center">
                  <img
                    className="block h-8 w-auto"
                    src={logo}
                    alt="JSW風車点検システム"
                  />
                </div>
                <div className="hidden md:block">
                  <div className="ml-10 flex items-baseline space-x-4">
                    {routes.map((route, key) => {
                      if (
                        route.group === "Admin" &&
                        loggedUserGroup !== "Admin"
                      ) {
                        return <div key={key}></div>;
                      } else {
                        return (
                          <NavLink
                            key={key}
                            className={({ isActive }) =>
                              isActive
                                ? "px-3 py-2 rounded-md text-sm font-medium text-white bg-teal-900"
                                : "px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-teal-700"
                            }
                            to={route.path}
                            end
                          >
                            {route.name}
                          </NavLink>
                        );
                      }
                    })}
                  </div>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="ml-4 flex items-center md:ml-6">
                  {/* <!-- Profile dropdown --> */}
                  <div className="ml-3 relative">
                    <LoggedUserDropdown />
                  </div>
                </div>
              </div>
              <div className="-mr-2 flex md:hidden">
                {/* <!-- Mobile menu button --> */}
                <MobileMenuButton />
              </div>
            </div>
          </div>
        </div>

        {/* <!-- Mobile menu --> */}
        <div
          className={`${
            isMenuOn ? "block" : "hidden"
          } border-b border-teal-700 md:hidden`}
        >
          <div className="px-2 py-3 space-y-1 sm:px-3">
            {routes.map((route, key) => (
              <NavLink
                key={key}
                className={({ isActive }) =>
                  isActive
                    ? "block px-3 py-2 rounded-md text-base font-medium text-white bg-teal-900"
                    : "block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-teal-700"
                }
                to={route.path}
                end
              >
                {route.name}
              </NavLink>
            ))}
          </div>
          <div className="pt-4 pb-3 border-t border-teal-700">
            <div className="flex items-center px-5">
              <div className="flex-shrink-0">
                <div className="bg-white h-10 w-10 rounded-full flex">
                  <p className="text-center text-2xl m-auto">
                    {loggedUser.length > 0 &&
                      loggedUser.charAt(0).toUpperCase()}
                  </p>
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium leading-none text-white">
                  {loggedUser}
                </div>
              </div>
            </div>
            <div className="mt-3 px-2 space-y-1">
              {/* <AmplifySignOut buttonText="ログアウト" /> */}
              <button
                type="button"
                className="w-full px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                onClick={() => signOut()}
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumbs */}
      {props.breadcrumbs != null && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 print:hidden">
          <nav className="sm:hidden" aria-label="Back">
            <button
              onClick={() => history(-1)}
              className="flex items-center text-sm font-medium text-gray-400 hover:text-gray-200"
            >
              {/* <!-- Heroicon name: chevron-left --> */}
              <ChevronLeftIcon className="flex-shrink-0 -ml-1 mr-1 h-4 w-4 text-gray-500" />
              戻る
            </button>
          </nav>
          <nav className="hidden sm:flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              {props.breadcrumbs.map((breadcrumb, key) => {
                const isFirst = key === 0;
                const isLast = key === props.breadcrumbs.length - 1;
                const backTo = (props.breadcrumbs.length - 1 - key) * -1;
                if (isFirst) {
                  return (
                    <li key={key}>
                      <div>
                        <button
                          onClick={() => history(backTo)}
                          className="text-sm font-medium text-gray-400 hover:text-gray-200"
                        >
                          {breadcrumb}
                        </button>
                      </div>
                    </li>
                  );
                } else if (isLast) {
                  return (
                    <li key={key}>
                      <div className="flex items-center">
                        <ChevronRightIcon className="flex-shrink-0 h-4 w-4 text-gray-500" />
                        <button className="ml-4 text-sm font-medium text-gray-400 hover:text-gray-200">
                          {breadcrumb}
                        </button>
                      </div>
                    </li>
                  );
                } else {
                  return (
                    <li key={key}>
                      <div className="flex items-center">
                        <ChevronRightIcon className="flex-shrink-0 h-4 w-4 text-gray-500" />
                        <button
                          onClick={() => history(backTo)}
                          className="ml-4 text-sm font-medium text-gray-400 hover:text-gray-200"
                        >
                          {breadcrumb}
                        </button>
                      </div>
                    </li>
                  );
                }
              })}
            </ol>
          </nav>
        </div>
      )}
      {/* Header Title */}
      <header className={props.breadcrumbs == null ? "py-10" : "pt-5 pb-10"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="inline-flex text-3xl font-bold text-white print:text-black">
            {props.title}
          </h1>
          {props?.printButton}
        </div>
      </header>
    </div>
  );
};

export default Header;
